import axios from 'axios';

// Thin apisauce-compatible wrapper around axios. Preserves the API surface
// our services rely on (create/get/post/put/delete/addMonitor) without
// pulling in apisauce, whose pinned axios version was a source of repeated
// security advisories.
//
// Behaviors that mirror apisauce:
//   - validateStatus: () => true — non-2xx responses resolve instead of
//     throwing. Callers branch on response.status.
//   - delete(url, params, config) — apisauce's 3-arg signature where
//     `params` is the query string and `config` carries the body.
//   - addMonitor(fn) — fn runs on every response without transforming it.
//   - setHeader/setHeaders/deleteHeader — mutate the instance default headers
//     so subsequent requests pick up auth tokens set after construction.

export const create = config => {
  const instance = axios.create({
    ...config,
    validateStatus: () => true
  });

  return {
    axiosInstance: instance,
    get: (url, params, axiosConfig) => instance.get(url, { params, ...axiosConfig }),
    post: (url, data, axiosConfig) => instance.post(url, data, axiosConfig),
    put: (url, data, axiosConfig) => instance.put(url, data, axiosConfig),
    patch: (url, data, axiosConfig) => instance.patch(url, data, axiosConfig),
    delete: (url, params, axiosConfig) => instance.delete(url, { params, ...axiosConfig }),
    addMonitor: monitor =>
      instance.interceptors.response.use(response => {
        monitor(response);
        return response;
      }),
    setHeader: (name, value) => {
      // Write to both common and top-level. axios v1 stores headers passed to
      // axios.create({ headers }) at the top level of defaults.headers, and
      // top-level wins over common when the request config is merged — so
      // updating only common would leave the original create-time value in
      // place. Writing to both keeps later updates effective regardless of
      // where the original landed.
      instance.defaults.headers.common[name] = value;
      instance.defaults.headers[name] = value;
    },
    setHeaders: headers => {
      Object.entries(headers).forEach(([name, value]) => {
        instance.defaults.headers.common[name] = value;
        instance.defaults.headers[name] = value;
      });
    },
    deleteHeader: name => {
      delete instance.defaults.headers.common[name];
      delete instance.defaults.headers[name];
    }
  };
};

export default { create };
